import React from 'react'

import HeaderMeta from 'components/HeaderMeta'

import Content, { Theme as ContentTheme } from 'components/base/Content'
import Title from 'components/base/Title'
import Paragraph, { Theme as ParagraphTheme } from 'components/base/Paragraph'
import Link, { Theme as LinkTheme } from 'components/base/Link'
import List, { Theme as ListTheme } from 'components/base/List'

import PageHero, { Theme as PageHeroTheme } from 'components/blocks/PageHero'
import Faq from 'components/blocks/Faq'
import AskQuestion from 'components/blocks/AskQuestion'
import ContactInfo from 'components/blocks/ContactInfo'

const SupportScreen: React.FC = (): React.ReactElement => (
  <div>
    <HeaderMeta
      title='Frequently Asked Questions | Insurance Lounge'
      description='Have questions about what policy is right for you? Visit our support page for frequently asked questions or ask us a question directly!'
    />
    <PageHero theme={PageHeroTheme.support}>Frequently Asked Questions About Insurance Coverage</PageHero>
    <Content short={true} center={true} margined={true}>
      <Title level={2}>How can we help?</Title>
      <Paragraph theme={ParagraphTheme.introLarge}>
        At Insurance Lounge, we’re happy to help answer questions and work with you to find the best coverage to meet your needs. Please check out the following resources.
      </Paragraph>
    </Content>
    <Content theme={ContentTheme.yellow}>
      <Content short={true} center={true}>
        <Title level={2}>Frequently Asked Questions</Title>
        <Paragraph>
          Please browse our FAQs for information on different insurance categories.
        </Paragraph>
        <Faq
          items={[
            {
              question: 'Auto Insurance FAQs',
              answer: (
                <>
                  <Title level={4}>Is Auto Insurance Required?</Title>
                  <Paragraph>
                    In most states, having some amount of liability insurance coverage is mandatory.
                    New Hampshire and Virginia are the only two states that do not mandate <Link theme={LinkTheme.default} to='/products/auto-insurance'>auto insurance</Link>.
                    Some states also require uninsured or underinsured motorist coverage or additional medical coverage.
                  </Paragraph>
                  <Title level={4}>
                    Is My Auto Insurance Rate Affected By My Vehicle?
                  </Title>
                  <Paragraph>
                    Yes. Vehicles with a higher market value should carry a higher amount of coverage, which will typically cost more.
                    Some types of vehicles with a
                    history of being driven more dangerously can also cost more to insure. Some newer vehicles have extra safety features that may qualify for a discounted rate, depending on the carrier.
                  </Paragraph>
                  <Title level={4}>How Much Coverage Is Necessary?</Title>
                  <Paragraph>
                    Some states specify a minimum amount of auto insurance for drivers. If not, it is wise to at least have liability car insurance.
                    Uninsured and underinsured motorist coverage is usually fairly inexpensive to add to a policy, and it can pay for itself many times over in accidents involving uninsured drivers.
                    While comprehensive or complete coverage may cost more, it’s often worth it.
                  </Paragraph>
                </>
              ),
            },
            {
              question: 'Home Insurance FAQs',
              answer: (
                <>
                  <Title level={4}>Are There Different Types of Deductibles?</Title>
                  <Paragraph>
                    There are three main types of home insurance deductibles:
                  </Paragraph>
                  <List theme={ListTheme.contentList}>
                    <li>
                      <strong>Percentage deductible</strong> - a certain percent based on dwelling coverage; for example, a one percent deductible on a home with dwelling coverage of $200,000 is $2,000
                    </li>
                    <li>
                      <strong>Flat deductible</strong> - a specific dollar amount;
                      for example, $3,000
                    </li>
                    <li>
                      <strong>Split deductible</strong> - different types of deductibles for different types of loss;
                      for example, a percent deductible may be in place for fire damages while a flat deductible applies to all other covered losses.
                    </li>
                  </List>
                  <Paragraph>
                    Whichever option you choose for <Link theme={LinkTheme.default} to='/products/homeowners-insurance'>homeowners insurance</Link>, the higher your deductible, the more you should expect to pay out of pocket. Lower deductibles typically result in paying a higher premium.
                  </Paragraph>
                  <Title level={4}>What Types of Valuable Are Covered?</Title>
                  <Paragraph>
                    Due to their high cost, home insurance for valuables is typically limited.
                    You may consider purchasing additional coverage for personal valuables such as jewelry, fine arts, or electronics if
                    they are worth more than the coverage limits of your policy.
                  </Paragraph>
                  <Title level={4}>When Should Additional Coverage Be Added?</Title>
                  <Paragraph>
                    Anytime something significant is added to your home or belongings, it’s a good idea to reassess how much coverage you need.
                    For example, after you complete a home addition or purchase new valuables, you may want to re-evaluate the coverage under your
                    current home insurance policy. Though many people don’t think about increasing
                    their homeowner’s insurance during these times, doing so can end up saving you money in the event of a loss.
                  </Paragraph>
                </>
              ),
            },
            {
              question: 'Life Insurance FAQs',
              answer: (
                <>
                  <Paragraph>
                    Deciding whether or not to <Link theme={LinkTheme.default} to='/products/life-insurance'>purchase life insurance</Link> is an intensely personal matter.
                    It can also be confusing to figure out what type and amount of insurance is best
                    for your situation. Use this helpful question and answer guide to learn more about this important insurance product.
                  </Paragraph>
                  <Title level={4}>What is Life Insurance?</Title>
                  <Paragraph>
                    Life insurance is a contract between an insurer and an insurance policyholder.
                    As part of the contract, the insurer agrees to pay a sum of money to the designated beneficiary upon the death of the insurance policyholder in
                    exchange for a premium. The premium is paid by the insurance policyholder or other designated payer.
                  </Paragraph>
                  <Title level={4}>Who Needs It?</Title>
                  <Paragraph>
                    Generally, if you have any dependents, you should have life insurance.
                    Dependents can be a spouse, young children, or older children who still live at home. Even an aging parent or sibling can qualify as a dependent.
                    If you do not have any dependents, life insurance can still fulfill a need by reducing the risk
                    associated with a loss of income that would otherwise be used to pay off existing debt, like a mortgage.
                  </Paragraph>
                  <Title level={4}>At What Age Should I Get a Policy?</Title>
                  <Paragraph>
                    A policy is generally less expensive the younger you are. Although earlier in life is better, it is never too late to purchase a policy
                    that will give your loved ones financial security. Keep in mind that more than half of the current life insurance policyholders in the U.S. are over 45.
                    Talk to your life insurance provider about the policy types offered.
                  </Paragraph>
                </>
              ),
            },
            {
              question: 'Health Insurance FAQs',
              answer: (
                <>
                  <Paragraph>
                    Whether you are a young person getting your own health insurance for the first time or an
                    older individual who has questions about insurance, here are a few questions and answers that you may find helpful.
                  </Paragraph>
                  <Title level={4}>What Is It?</Title>
                  <Paragraph>
                    <Link theme={LinkTheme.default} to='/products/health-insurance'>Health insurance</Link> covers a portion of or all the financial risk associated with your medical expenses. In exchange for a premium, the insurer agrees to pay some (or all) of the insured’s healthcare expenses.
                  </Paragraph>
                  <Title level={4}>When Can I Enroll?</Title>
                  <Paragraph>
                    Generally, you are required to sign up for an insurance policy during an open enrollment period.
                    This period usually occurs at the end of the year. You can also enroll or make changes to your policy if you experience a qualifying life event,
                    including getting married, gaining a dependent, getting divorced, or losing a covered family member. Talk to your health insurance provider if you aren’t sure
                    which events qualify for insurance changes outside the open enrollment period.
                  </Paragraph>
                  <Title level={4}>Which Type of Policy Should I Get?</Title>
                  <Paragraph>
                    There are five different health insurance types:
                  </Paragraph>
                  <List theme={ListTheme.contentList}>
                    <li>
                      High-deductible health plans (HDHPS), which can be paired with
                      health savings accounts
                    </li>
                    <li>Exclusive provider organizations (EPOs)</li>
                    <li>Health maintenance organizations (HMOs)</li>
                    <li>Point-of-service (POS) plans</li>
                  </List>
                  <Paragraph>
                    Talk to your health insurance provider to learn more about these plans and find out which type of
                    insurance will best meet your needs.
                  </Paragraph>
                </>
              ),
            },
            {
              question: 'Medicare Insurance FAQs',
              answer: (
                <>
                  <Paragraph>
                    If you struggle with health issues or are growing older, you may be wondering if
                    Medicare insurance is an option for you. Here are a few common questions and answers about
                    this national health insurance program.
                  </Paragraph>
                  <Title level={4}>What Is Medicare?</Title>
                  <Paragraph>
                    Medicare is a U.S. health insurance program that is intended for people aged 65 and older,
                    as well as a few other groups of qualifying people. There are different parts of <Link theme={LinkTheme.default} to='/products/medicare-insurance'>Medicare</Link> that offer
                    coverage for different services. Here’s a brief overview of the different sections:
                  </Paragraph>
                  <List>
                    <li>Part A (Hospital Insurance)</li>
                    <li>Part B (Medical Insurance)</li>
                    <li>Part D (Prescription Drug Coverage)</li>
                  </List>
                  <Paragraph>
                    Insurance companies approved by Medicare offer separate plans to supplement these three parts.
                    You may also be eligible for Medicare Advantage, which is an alternative to Original Medicare and includes bundled plans.
                  </Paragraph>
                  <Title level={4}>Am I Eligible?</Title>
                  <Paragraph>
                    Anyone over 65 years old is generally eligible for Medicare. In addition, people with
                    End-Stage Renal Disease and younger people with certain disabilities may be eligible for coverage.
                  </Paragraph>
                  <Paragraph>
                    You may be automatically enrolled in Medicare when you’re eligible if you already receive Railroad Retirement Board or Social Security benefits.
                    Otherwise, you may need to enroll during your seven-month Initial Enrollment Period.
                    Talk to your Medicare insurance provider if you have questions about enrollment.
                  </Paragraph>
                  <Title level={4}>How Are Medicare and Medicaid Different?</Title>
                  <Paragraph>
                    Medicare is intended for anyone 65 years of age and older (as well as a few other qualifying groups).
                    Medicaid is meant to provide insurance coverage for those with very low incomes.
                    It is possible to be eligible for both programs.
                  </Paragraph>
                </>
              ),
            },
            {
              question: 'Business Insurance FAQs',
              answer: (
                <>
                  <Title level={4}>What Business Insurance Should I Have?</Title>
                  <Paragraph>
                    While you can always add more insurance, most businesses should consider their needs for
                    at least the following types of <Link theme={LinkTheme.default} to='/products/business-insurance'>business insurance</Link>:
                  </Paragraph>
                  <List>
                    <li>General liability insurance</li>
                    <li>Business interruption insurance</li>
                    <li>Business personal property insurance</li>
                    <li>
                      Commercial auto insurance (if the business involves a driving
                      risk)
                    </li>
                  </List>
                  <Paragraph>
                    If you run your business from home, home-based business insurance is also a must.
                    Every business will have different insurance needs, and a professional business insurance
                    provider can assess your needs and customize a policy with adequate coverage.
                  </Paragraph>
                  <Title level={4}>Is Business Insurance Mandatory?</Title>
                  <Paragraph>
                    Each business is different, and certain types of insurance are mandatory only in specific situations. However, there are business insurance policies
                    that are required for most businesses. Some types of business insurance that can be mandatory include:
                  </Paragraph>
                  <List>
                    <li>Worker’s compensation insurance</li>
                    <li>Unemployment insurance</li>
                    <li>Disability insurance</li>
                    <li>Professional liability insurance</li>
                  </List>
                  <Paragraph>
                    Operating without these policies in place can have legal and
                    monetary consequences.
                  </Paragraph>
                  <Title level={4}>
                    Are Lawsuits Covered by Business Insurance?
                  </Title>
                  <Paragraph>
                    When the necessary business liability insurance is in place with enough coverage for legal costs,
                    business insurance will pay for defense against legal action. Additional policies may be needed to
                    cover defense costs or damages in excess of underlying policy limits. Lawsuits resulting from
                    expected or intentionally caused injuries or damages are generally excluded from liability coverage.
                  </Paragraph>
                </>
              ),
            },
            {
              question: 'Disability Insurance FAQs',
              answer: (
                <>
                  <Title level={4}>Who Needs Disability Insurance?</Title>
                  <Paragraph>
                    If you depend on income from a job, you should consider disability insurance as protection against the financial risk that comes
                    from a serious injury and its effect on your employment. Replacing income via disability
                    insurance if you’re hurt or ill and cannot work is one way to help maintain financial stability.
                  </Paragraph>
                  <Title level={4}>How Does Disability Insurance Work?</Title>
                  <Paragraph>
                    <Link theme={LinkTheme.default} to='/products/disability-insurance'>Disability insurance</Link> will pay out claims benefits based on a few different provisions within a disability policy, including:
                  </Paragraph>
                  <List>
                    <li>Benefit amount (often a percentage of income)</li>
                    <li>Benefit period (length of time the benefits are paid)</li>
                    <li>
                      Exclusion period (how long an injury or illness must last
                      before benefits are paid)
                    </li>
                    <li>
                      Any included riders (endorsements to the policy that affect
                      benefits)
                    </li>
                  </List>
                  <Paragraph>
                    You should consider your financial needs, occupation, and risk
                    factors like age and health when evaluating how much disability
                    insurance to purchase.
                  </Paragraph>
                  <Title level={4}>
                    Can I Get Disability Insurance Through My Employer?
                  </Title>
                  <Paragraph>
                    Many employers offer disability insurance to their employees,
                    either as part of their benefits package or as an optional
                    add-on. However, disability coverage offered by an employer
                    generally does not stay with an employee when they leave the
                    company, and there may be more restrictions around benefit
                    amounts, exclusion periods, or rider options.
                  </Paragraph>
                  <Paragraph>
                    To help supplement or replace coverage offered by an employer,
                    you can purchase individual disability insurance. These policies
                    are held by the purchaser and generally offer more flexibility in
                    terms of how much coverage one can obtain.
                  </Paragraph>
                </>
              ),
            },
            {
              question: 'Investments & Retirement FAQs',
              answer: (
                <>
                  <Title level={4}>Why Use an Investment Professional?</Title>
                  <Paragraph>
                    Working with an investment professional can allow you to establish goals and find the best strategy to meet them. Investment
                    professionals are trained and educated to recommend the right type of investments for your needs and help you <Link theme={LinkTheme.default} to='/products/investments-retirement'>plan for a comfortable retirement</Link>.
                    When someone is monitoring your account, you can be quickly alerted when changes need to be made or when personal or economic situations
                    arise or you need to reassess your investments.
                  </Paragraph>
                  <Title level={4}>What Is a Mutual Fund?</Title>
                  <Paragraph>
                    An investment portfolio of stocks, bonds, and other assets provides you with diversification
                    so that you have the security of stable investments, as well as the potential gains of more risky assets. An investment where your money
                    is combined with money from other investors to increase your buying power is known as a mutual fund.
                  </Paragraph>
                  <Title level={4}>
                    What Is the Difference Between Stocks and Bonds?
                  </Title>
                  <Paragraph>
                    When you purchase a stock, you own a portion of a corporation. As you purchase more shares of a particular stock,
                    you own a larger percentage of that corporation. Purchasing a bond is basically loaning money to the issuer of the bond. You will receive
                    scheduled interest payments on the price of the bond until it reaches its maturity date, at which point you will receive the principal amount back from the issuer.
                  </Paragraph>
                </>
              ),
            },
          ]}
        />
      </Content>
    </Content>
    <Content short={true} center={true} margined={true}>
      <Paragraph>At Insurance Lounge, we provide a wide variety of insurance coverage options. Get a quote for any of the policies listed above by using our convenient online <Link theme={LinkTheme.default} to='/quote/new'>quote and buy tool</Link> today.</Paragraph>
    </Content>
    <AskQuestion title='We’re Here, When You Need Us.'>
      Reach out and an Insurance Concierge<sup>®</sup> will get in touch!
    </AskQuestion>
    <ContactInfo />
  </div>
)

export default SupportScreen
